/**
 * TextBlockSection — renders the full TextBlockConfig from the page builder.
 * Backward-compatible: old configs with {title, body, alignment} still render.
 */

import React, { useEffect, useRef } from 'react';

// ─── List icon helpers ────────────────────────────────────────────────────────

const LIST_ICON = {
  check: (color) => (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="8" cy="8" r="8" fill={color || '#10b981'} opacity="0.15" />
      <path d="M4.5 8.5l2.5 2.5 4-5" stroke={color || '#10b981'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  arrow: (color) => (
    <span style={{ color: color || '#7c3aed', flexShrink: 0, fontSize: '1em', marginTop: 1 }}>→</span>
  ),
};

const LIST_SPACING = { tight: '0.25em', normal: '0.6em', loose: '1em' };

// ─── Animation keyframes (injected once) ──────────────────────────────────────

const ANIM_STYLES = `
@keyframes tb-fade-in    { from { opacity:0 }                   to { opacity:1 } }
@keyframes tb-slide-up   { from { opacity:0; transform:translateY(24px) } to { opacity:1; transform:translateY(0) } }
@keyframes tb-slide-left { from { opacity:0; transform:translateX(-24px)} to { opacity:1; transform:translateX(0) } }
@keyframes tb-slide-right{ from { opacity:0; transform:translateX(24px) } to { opacity:1; transform:translateX(0) } }
@keyframes tb-zoom-in    { from { opacity:0; transform:scale(0.95) }      to { opacity:1; transform:scale(1) } }
`;

const ANIM_MAP = {
  'fade-in':     'tb-fade-in',
  'slide-up':    'tb-slide-up',
  'slide-left':  'tb-slide-left',
  'slide-right': 'tb-slide-right',
  'zoom-in':     'tb-zoom-in',
};

export default function TextBlockSection({ config, theme }) {
  const isLegacy = config && !('blockType' in config);
  const animInjected = useRef(false);

  // Inject animation keyframes once
  useEffect(() => {
    if (animInjected.current) return;
    const style = document.createElement('style');
    style.textContent = ANIM_STYLES;
    document.head.appendChild(style);
    animInjected.current = true;
  }, []);

  // ── Legacy config (title / body / alignment) ──────────────────────────────
  if (isLegacy) {
    const { title = '', body = '', alignment = 'left' } = config;
    return (
      <div style={{ textAlign: alignment }}>
        {title && <h2 style={{ fontWeight: 700, fontSize: '1.75rem', marginBottom: '0.75rem' }}>{title}</h2>}
        {body && <div style={{ fontSize: '1rem', lineHeight: 1.8, color: '#64748b', whiteSpace: 'pre-wrap' }}>{body}</div>}
      </div>
    );
  }

  // ── Rich config ────────────────────────────────────────────────────────────
  const cfg = config || {};

  // Visibility
  if (cfg.visibleOnDesktop === false) return null; // client is always "desktop" here; CSS handles the rest

  // Typography base style
  const fontStyle = {
    fontFamily:      cfg.fontFamily ? `"${cfg.fontFamily}", sans-serif` : 'inherit',
    fontWeight:      cfg.fontWeight  || 400,
    fontStyle:       cfg.fontStyle   || 'normal',
    fontSize:        cfg.fontSize    ? `${cfg.fontSize}px` : 'inherit',
    lineHeight:      cfg.lineHeight  || 1.7,
    letterSpacing:   cfg.letterSpacing ? `${cfg.letterSpacing}em` : 'normal',
    textTransform:   cfg.textTransform || 'none',
    textDecoration:  cfg.textDecoration || 'none',
    textAlign:       cfg.textAlign   || 'left',
    direction:       cfg.direction   || 'ltr',
    color:           cfg.textColor   || '#374151',
    margin:          0,
    padding:         0,
  };

  // Gradient text
  if (cfg.gradientText && cfg.gradientFrom && cfg.gradientTo) {
    Object.assign(fontStyle, {
      background: `linear-gradient(${cfg.gradientAngle || 135}deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent',
    });
  }

  // Spacing & layout
  const blockAlign = cfg.horizontalAlign === 'center' ? '0 auto'
    : cfg.horizontalAlign === 'right' ? '0 0 0 auto'
    : undefined;

  const wrapStyle = {
    marginTop:    cfg.marginTop    ? `${cfg.marginTop}px`    : undefined,
    marginBottom: cfg.marginBottom ? `${cfg.marginBottom}px` : undefined,
    paddingTop:   cfg.paddingTop   ? `${cfg.paddingTop}px`   : undefined,
    paddingBottom: cfg.paddingBottom ? `${cfg.paddingBottom}px` : undefined,
    paddingLeft:  cfg.paddingLeft  ? `${cfg.paddingLeft}px`  : undefined,
    paddingRight: cfg.paddingRight ? `${cfg.paddingRight}px` : undefined,
    maxWidth:     typeof cfg.maxWidth === 'number' ? `${cfg.maxWidth}px` : undefined,
    margin:       blockAlign,
    display:      'block',
    width:        '100%',
  };

  // Background
  if (cfg.bgVisible && cfg.bgColor) {
    const opacity = (cfg.bgOpacity ?? 100) / 100;
    const hex = cfg.bgColor.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    wrapStyle.background = `rgba(${r},${g},${b},${opacity})`;
  }

  // Border
  if (cfg.borderVisible) {
    const bw = cfg.borderWidth || 1;
    const bc = cfg.borderColor || '#e2e8f0';
    const bs = cfg.borderStyle || 'solid';
    const side = cfg.borderSide || 'all';
    if (side === 'all') {
      wrapStyle.border = `${bw}px ${bs} ${bc}`;
    } else {
      const prop = `border${side.charAt(0).toUpperCase() + side.slice(1)}`;
      wrapStyle[prop] = `${bw}px ${bs} ${bc}`;
    }
    if (cfg.borderRadius) wrapStyle.borderRadius = `${cfg.borderRadius}px`;
  }

  // Animation
  const animName = ANIM_MAP[cfg.entranceAnimation];
  if (animName) {
    wrapStyle.animation = `${animName} ${cfg.animationDuration || 600}ms ease ${cfg.animationDelay || 0}ms both`;
  }

  // Link + highlight styles injected inline via <style>
  const scopeId = cfg.id ? `#${cfg.id}` : `.tb-${cfg.customClassName || 'block'}`;
  const inlineStyles = `
    ${scopeId} a { color: ${cfg.linkColor || '#2563eb'}; text-decoration: ${cfg.linkUnderline ? 'underline' : 'none'}; }
    ${scopeId} a:hover { color: ${cfg.linkHoverColor || '#1d4ed8'}; }
    ${cfg.highlightVisible ? `${scopeId} mark { background: ${cfg.highlightColor || '#fde68a'}; border-radius: 2px; padding: 0 3px; }` : ''}
  `;

  // ── List rendering ──────────────────────────────────────────────────────────
  if (cfg.blockType === 'list') {
    const items = cfg.listItems || [];
    const gap = LIST_SPACING[cfg.listSpacing] || LIST_SPACING.normal;
    const isNumbered = cfg.listStyle === 'numbered';
    const isCustomIcon = cfg.listStyle === 'check' || cfg.listStyle === 'arrow';

    return (
      <div style={wrapStyle} id={cfg.id || undefined} className={cfg.customClassName || undefined}
        aria-label={cfg.ariaLabel || undefined} role={cfg.role || undefined}>
        <style>{inlineStyles}</style>
        {isNumbered ? (
          <ol style={{ ...fontStyle, paddingLeft: '1.5em', margin: 0 }}>
            {items.map((item, i) => (
              <li key={i} style={{ marginBottom: gap }}>{item}</li>
            ))}
          </ol>
        ) : isCustomIcon ? (
          <ul style={{ ...fontStyle, listStyle: 'none', padding: 0, margin: 0 }}>
            {items.map((item, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6em', marginBottom: gap }}>
                {cfg.listStyle === 'check' ? LIST_ICON.check(cfg.listIconColor) : LIST_ICON.arrow(cfg.listIconColor)}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ul style={{ ...fontStyle, paddingLeft: '1.5em', margin: 0 }}>
            {items.map((item, i) => (
              <li key={i} style={{ marginBottom: gap }}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ── Quote rendering ─────────────────────────────────────────────────────────
  if (cfg.blockType === 'quote') {
    const accent = cfg.quoteAccentColor || '#7c3aed';
    const qs = cfg.quoteStyle || 'left-border';

    const quoteWrapStyle = { ...wrapStyle };
    if (qs === 'left-border') {
      Object.assign(quoteWrapStyle, { borderLeft: `4px solid ${accent}`, paddingLeft: 20 });
    } else if (qs === 'large-quote-mark') {
      Object.assign(quoteWrapStyle, { position: 'relative', paddingTop: 24, paddingLeft: 8 });
    } else if (qs === 'centered') {
      Object.assign(quoteWrapStyle, { textAlign: 'center', padding: '16px 0' });
      fontStyle.textAlign = 'center';
    } else if (qs === 'card') {
      Object.assign(quoteWrapStyle, { background: '#f8fafc', borderRadius: 8, padding: 20, borderLeft: `4px solid ${accent}` });
    }

    return (
      <div style={quoteWrapStyle} id={cfg.id || undefined} className={cfg.customClassName || undefined}
        aria-label={cfg.ariaLabel || undefined} role={cfg.role || 'blockquote'}>
        <style>{inlineStyles}</style>
        {qs === 'large-quote-mark' && (
          <span style={{ position: 'absolute', top: 0, left: 0, fontSize: '4rem', lineHeight: 1, color: accent, opacity: 0.25, fontFamily: 'serif' }}>"</span>
        )}
        <blockquote style={{ ...fontStyle, margin: 0, border: 'none', padding: 0 }}
          dangerouslySetInnerHTML={{ __html: cfg.text || '' }} />
        {cfg.quoteAuthorVisible && (cfg.quoteAuthor || cfg.quoteAuthorRole) && (
          <footer style={{ marginTop: 12, fontSize: '0.9em', color: cfg.textColor || '#64748b', opacity: 0.8, fontStyle: 'normal' }}>
            {cfg.quoteAuthor && <strong>{cfg.quoteAuthor}</strong>}
            {cfg.quoteAuthor && cfg.quoteAuthorRole && ', '}
            {cfg.quoteAuthorRole && <span>{cfg.quoteAuthorRole}</span>}
          </footer>
        )}
      </div>
    );
  }

  // ── Default: paragraph / heading / label / caption / subheading ────────────
  const Tag = cfg.htmlTag || 'p';

  return (
    <div style={wrapStyle} id={cfg.id || undefined} className={cfg.customClassName || undefined}>
      <style>{inlineStyles}</style>
      <Tag
        style={fontStyle}
        aria-label={cfg.ariaLabel || undefined}
        role={cfg.role || undefined}
        dangerouslySetInnerHTML={{ __html: cfg.text || '' }}
      />
    </div>
  );
}
